import React, { Component } from 'react'

import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider'
import getMuiTheme from 'material-ui/styles/getMuiTheme'
import Paper from 'material-ui/Paper'
import Slider from 'material-ui/Slider'
import IconButton from 'material-ui/IconButton'
import FontIcon from 'material-ui/FontIcon'
import FloatingActionButton from 'material-ui/FloatingActionButton'
import LinearProgress from 'material-ui/LinearProgress'
import styled from 'styled-components'
import { css } from 'styled-components'
import MetaTags from 'react-meta-tags'

import getColors from './player_get_colors'

const sizes = {
  small: 576,
  xmedium: 768,
  xlarge: 992
}

// Iterate through the sizes and create a media template
const media = Object.keys(sizes).reduce((acc, label) => {
acc[label] = (...args) => css`
    @media (min-width: ${sizes[label] / 16}em) {
      ${css(...args)}
    }
  `
return acc
}, {})

// GENERAL

const StyledPlayer = styled.div`
  position: relative;
  overflow: hidden;
`
const StyledContainer = styled.div`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
`
const cardStyle = {
  display: 'flex',
  WebkitFlexDirection: 'row',
  msFlexDirection: 'row',
  flexDirection: 'row',
  WebkitFlexWrap: 'nowrap',
  msFlexWrap: 'nowrap',
  flexWrap: 'nowrap',
  WebkitFlexShrink: '0',
  msFlexNgative: '0',
  flexShrink:'0',
  boxSizing: 'border-box',
  WebkitAlignSelf: 'stretch',
  msFlexItemAlign: 'stretch',
  alignSelf: 'stretch',
  WebkitAlignItems: 'center',
  msFlexAlign: 'center',
  alignItems: 'center',
}

// COVER

const coverStyle = {
  WebkitBoxFlex: 1,
  msFlex: 'auto',
  flex: 'auto',
  backgroundColor: '#9e9e9e',
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: '0px !important',
}
const fabStyle = {
  position: 'absolute',
  zIndex: '6',
  right: '16px',
  bottom: '-28px',
}

// TRACK INFO

const trackStyle = {
  minHeight: '0',
  padding: '32px 16px',
  width: '100%',
  overflow: 'visible',
  borderRadius: '0px !important',
  position: 'relative',
}

const StyledTitle = styled.h3`
  font-family: "Roboto","Helvetica","Arial",sans-serif;
  font-weight: 400;
  font-size: 1.8em;
  line-height: 1.2em;
  margin: 0 24px 4px;
  padding: 0;
  max-height: 2.4em;
  overflow: hidden;
  ${media.xmedium`
    font-size: 2em;
    margin: 0 40px 4px;
  `}
`
const StyledAuthor = styled.h4`
  font-family: "Roboto","Helvetica","Arial",sans-serif;
  font-weight: 300;
  font-size: 1.2em;
  line-height: 1.25em;
  margin: 0 24px;
  padding: 0;
  max-height: 2.5em;
  overflow: hidden;
  ${media.xmedium`
    font-size: 1.4em;
    margin: 0 40px;
  `}
`

// VOLUME

const StyledClickContainer = styled.div`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  -webkit-tap-highlight-color: rgba(0,0,0,0);
  display: ${props => props.volOpen ? 'block' : 'none'};
`
const volumeStyle = {
  position: 'absolute',
  zIndex: '6',
  width: '56px',
  height: '320px',
  bottom: 'calc(15px + env(safe-area-inset-bottom))',
  left: '10px',
  padding: '8px 0',
  transform: 'scaleY(0) scaleX(0)',
  transformOrigin: 'bottom',
  transition: 'all 0.2s ease-in-out',
  willChange: 'transform',
  ...cardStyle,
}
const volumeOpenStyle = {
  transform: 'scaleY(1) scaleX(1)',
}
volumeStyle.flexDirection = volumeStyle.WebkitFlexDirection = volumeStyle.msFlexDirection = 'column'
const StyledVolumeSliderContainer = styled.div`
  margin: 0 20px;
  -webkit-box-flex: 1;
  -ms-flex: auto;
  flex: auto;
  position: relative;
  margin: 20px 0;
  height: 152px;
  top: -24px;
`

// CONTROLS

const StyledTime = styled.h6`
  font-family: "Roboto","Helvetica","Arial",sans-serif;
  font-weight: 400;
  line-height: 24px;
  font-size: 16px;
  letter-spacing: .04em;
  margin: 0 16px 0 0;
`
const StyledLeftTime = styled.h6`
  font-family: "Roboto","Helvetica","Arial",sans-serif;
  font-weight: 400;
  line-height: 24px;
  font-size: 16px;
  letter-spacing: .04em;
  margin: 0 0 0 16px;
`
const StyledProgressContainer = styled.div`
  margin: 0 20px;
  -webkit-box-flex: 1;
  -ms-flex: auto;
  flex: auto;
  position: relative;
  top: 12px;
`
const controlStyle = {
  ...cardStyle,
  position: 'relative',
  borderRadius: '0px !important',
  padding: '0 16px',
  zIndex: '4',
  height: 'calc(64px + env(safe-area-inset-bottom))',
  paddingBottom: 'env(safe-area-inset-bottom)',
}

let timeout

class Player extends Component {
  
  constructor(props) {
    super(props);
    
    this.state = {
      volOpen: false,
      volume: this.props.volume,

      currentProgress: this.props.currentProgress,
      currentTime: this.convertToTime(this.props.currentProgress),
      maxTime: this.convertToTime(this.props.maxProgress),

      backgroundColor: "rgb(97,97,97)",
      trackColor: "#ffffff",

      accentColor: "#69f0ae",
      fabIconColor: "rgb(66, 66, 66)",

      coverUrl: null,

      dragging: false,
    }
  }

  componentWillUpdate(nextProps) {
    if (this.props.coverUrl !== nextProps.coverUrl){
      getColors(nextProps.coverUrl, this.handleColorChange)
    }
  }

  componentWillReceiveProps(nextProps) {

    if (nextProps.currentProgress && nextProps.maxProgress) {

      if (!this.state.dragging) this.setState({
        currentProgress: nextProps.currentProgress, 
        currentTime: this.convertToTime(nextProps.currentProgress),
        maxTime: this.convertToTime(nextProps.maxProgress)
      })

    }

    if (nextProps.coverUrl){
      this.setState({coverUrl: nextProps.coverUrl})
    }

    if (nextProps.volume){
      this.setState({volume: nextProps.volume})
    }
  }

  // METHODS

  convertToTime = (secs) => {
    var minutes = Math.floor(secs / 60);
    var seconds = (secs - minutes * 60).toFixed(0);
    return (seconds === 60 ? (minutes+1) + ":00" : (minutes < 10 ? "0" : "") + minutes + ":" + (seconds < 10 ? "0" : "") + seconds);
  }

  // HANDLERS

  handleColorChange = (backgroundColor, isBgDark, accentColor, isAccentDark) => {
    
    this.setState({
      backgroundColor,
      trackColor: isBgDark ? "#ffffff" : "#424242",
      accentColor,
      fabIconColor: isAccentDark ? "#ffffff" : "#424242",
    })
  }

  toggleVolumeOpen = () => {
    clearTimeout(timeout)

    if (!this.state.volOpen) {
      timeout = setTimeout(this.toggleVolumeOpen, 4000)
    }

    this.setState({volOpen: !this.state.volOpen})
  }

  // EVENTS

  onDragStart = () => {
    this.setState({dragging: true})
  }

  onProgressChange = (event, value) => {
    this.setState({
      currentProgress: value,
      currentTime: this.convertToTime(value),
      maxTime: this.convertToTime(this.props.maxProgress)
    })
  }

  onDragStop = () => {
    this.setState({dragging: false});
    this.props.onProgressChange(this.state.currentProgress)
  }

  onVolumeChange = (event, value) => {
    clearTimeout(timeout)
    timeout = setTimeout(this.toggleVolumeOpen, 2000)

    this.setState({volume: value})

    this.props.onVolumeChange(value)
  }

  // GETTERS

  get muiTheme() {
    const { accentColor, fabIconColor } = this.state
    return getMuiTheme({
      fontFamily: 'Roboto, sans-serif',
      palette: {
        primary1Color: '#9e9e9e',
        accent1Color: accentColor || '#69f0ae',
        textColor: '#666666',
      },
      floatingActionButton: {
        secondaryIconColor: fabIconColor || '#424242',
      },
    })
  }

  get muiThemeProgress() {
    const { accentColor } = this.state

    return getMuiTheme({
      palette: {
        primary1Color: accentColor || '#69f0ae'
      }
    })
  }

  render() {

    // Detect if ios to disable volume button
    const onIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

    // COVER IMAGE

    coverStyle.backgroundImage = `url(${this.props.coverUrl})`
    const coverImage = 
    <Paper style = {coverStyle}>
      <FloatingActionButton 
        disabled={this.props.maxProgress === 0} 
        secondary={true} 
        style={fabStyle} 
        onClick = {this.props.onTogglePlayPause}
      >
        <FontIcon className="material-icons">
          {
            this.state.currentTime === this.state.maxTime && this.props.maxProgress !== 0 ? 'replay'
            : this.props.isPlaying ? "pause" : "play_arrow"
          }
        </FontIcon>
      </FloatingActionButton>
    </Paper>

     // TRACK INFO

    trackStyle.backgroundColor = this.state.backgroundColor
    const trackInfo = 
    <Paper style = {trackStyle}>
      {this.props.loading ?
        <MuiThemeProvider muiTheme={this.muiThemeProgress}>
          <LinearProgress style={{position: 'absolute', left: 0, right: 0, top: 0, backgroundColor: '#fff'}} mode="indeterminate" />
        </MuiThemeProvider> 
      : null}
      <StyledTitle style = {{color: this.state.trackColor}} >{this.props.trackTitle}</StyledTitle>
      <StyledAuthor style = {{color: this.state.trackColor}}  >{this.props.trackAuthor}</StyledAuthor>
    </Paper>

    // VOLUME

    const volume =
    <div>
      <StyledClickContainer volOpen={this.state.volOpen} onClick = {this.toggleVolumeOpen} />
      <Paper 
      style={this.state.volOpen ? {...volumeStyle, ...volumeOpenStyle} : volumeStyle}
      zDepth={4}>
        <IconButton onClick={() => {this.onVolumeChange(null, 100)}}>
          <FontIcon className="material-icons">volume_up</FontIcon>
        </IconButton>
        <StyledVolumeSliderContainer>
          <Slider
          axis='y'
          style={{height: '100%'}}
          min={0}
          max={100}
          step={1}
          value={parseInt(this.state.volume, 10)}
          onChange={this.onVolumeChange}
          />
        </StyledVolumeSliderContainer>
        <IconButton onClick={() => {this.onVolumeChange(null, 0)}}>
          <FontIcon className="material-icons">volume_off</FontIcon>
        </IconButton>
      </Paper>
    </div>

    // CONTROLS
    let controls =
    <Paper style={controlStyle} zDepth={3}>
      {
        !onIos 
        ? <IconButton onClick = {this.toggleVolumeOpen} >
            <FontIcon className="material-icons">{this.state.volume === 0 ? "volume_off" : this.state.volume < 50 ? "volume_down" : "volume_up"}</FontIcon>
          </IconButton>
        : <StyledLeftTime>{this.state.currentTime}</StyledLeftTime>
      }
      <StyledProgressContainer>
        <Slider
        disabled={this.props.maxProgress === 0}
        min={0}
        max={this.props.maxProgress > 0 ? this.props.maxProgress : 1}
        value={this.state.currentProgress}
        onChange={this.onProgressChange}
        onDragStart={this.onDragStart}
        onDragStop={this.onDragStop}
        />
      </StyledProgressContainer>
      {
        !onIos 
        ? <StyledTime>{this.state.currentTime}</StyledTime> 
        : <StyledTime>{this.state.maxTime}</StyledTime>
      }
    </Paper>

    return (
      <MuiThemeProvider muiTheme={this.muiTheme}>
        <StyledPlayer style={{height: this.props.height, width: this.props.width}} >
          <MetaTags>
            <meta id="theme" name="theme-color" content={this.state.backgroundColor} />
          </MetaTags>
          <StyledContainer >
            {coverImage}
            {trackInfo}
            {volume}
            {controls}
          </StyledContainer>
        </StyledPlayer>
      </MuiThemeProvider>
    )
  }
}

export default Player


// WEBPACK FOOTER //
// ./src/components/player_material_ui.jsx